/**
 * FilterBar — Barra de filtros rápidos reutilizable.
 *
 * labelLayout:
 *  - 'inline' (default): rótulo a la izquierda del control (comportamiento histórico).
 *  - 'stacked': rótulo sobre el control (como el cajón de avanzados).
 */
import { useId } from 'react'
import { History, Search, SlidersHorizontal, X } from 'lucide-react'
import DatePicker from '@/components/DatePicker'
import { currentMonthRange, lastMonthRange, currentYearRange } from '@/lib/utils'
import { SegmentGroup } from './SegmentGroup'
import type { FilterDef } from './types'
import type { FilterConfig } from './types'
import type { useFilters } from './useFilters'

interface FilterBarProps {
  config: FilterConfig
  filters: ReturnType<typeof useFilters>
  onToggleAdvanced?: () => void
  labelLayout?: 'inline' | 'stacked'
}

export function FilterBar({ config, filters, onToggleAdvanced, labelLayout = 'inline' }: FilterBarProps) {
  const quickFilters = config.filter(f => f.placement === 'quick')
  const hasAdvanced = config.some(f => f.placement === 'advanced')
  const stacked = labelLayout === 'stacked'

  return (
    <>
      <div className={`flex flex-wrap gap-2 ${filters.searchOverridesDateRange ? 'mb-2' : 'mb-4'} ${stacked ? 'items-end' : 'items-center'}`}>
        {quickFilters.map(f => (
          <FilterControl key={f.key} def={f} filters={filters} stacked={stacked} />
        ))}

        {hasAdvanced && (
          <button
            onClick={onToggleAdvanced}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
          >
            <SlidersHorizontal size={13} />
            Filtros
            {filters.advancedFilterCount > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 bg-primary text-on-primary text-[10px] rounded-full leading-none">
                {filters.advancedFilterCount}
              </span>
            )}
          </button>
        )}

        {filters.hasActiveFilters && (
          <button
            onClick={filters.resetAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-content-muted border border-divider rounded-lg hover:bg-soft hover:text-danger-content-soft hover:border-danger-border transition-colors"
          >
            <X size={12} />
            Limpiar
          </button>
        )}
      </div>

      {filters.searchOverridesDateRange && (
        <div className="mb-4 flex items-center gap-1.5 text-xs text-content-muted" role="status">
          <History size={13} className="text-content-subtle shrink-0" aria-hidden="true" />
          <span>
            Buscando en <strong className="font-medium text-content">todo el historial</strong> — elige un rango de fecha para acotar la búsqueda.
          </span>
        </div>
      )}
    </>
  )
}

/**
 * FilterControl — renderiza UN filtro con los controles estándar del sistema
 * (search, select, segment, date-range con presets, number-range). Exportado
 * para que layouts a medida (p. ej. el toolbar de /sales/documents al estilo del
 * mockup) reusen exactamente los mismos controles y se mantenga la consistencia.
 */
export function FilterControl({ def, filters, stacked }: { def: FilterDef; filters: ReturnType<typeof useFilters>; stacked?: boolean }) {
  const uid = useId()

  // Control sin rótulo inline cuando se apila (el rótulo va arriba vía wrapper).
  const hideInlineLabel = !!stacked

  const control = (() => {
    switch (def.type) {
      case 'search':
        return (
          <div className={`relative flex-1 min-w-[180px] ${def.grow ? '' : 'max-w-xs'}`}>
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" aria-hidden="true" />
            <input
              id={uid}
              value={filters.searchRaw}
              onChange={e => filters.setFilter(def.key, e.target.value)}
              placeholder={def.placeholder ?? 'Buscar...'}
              aria-label={def.placeholder ?? 'Buscar'}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 transition-all placeholder:text-content-subtle"
            />
          </div>
        )

      case 'select':
        return (
          <div className="flex items-center gap-1.5">
            {!hideInlineLabel && def.label && <label htmlFor={uid} className="text-xs font-medium text-content-subtle">{def.label}</label>}
            <select
              id={uid}
              aria-label={def.label}
              value={filters.values[def.key] ?? ''}
              onChange={e => filters.setFilter(def.key, e.target.value)}
              className={`px-2.5 py-1.5 text-xs border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 transition-all ${def.widthClass ? `${def.widthClass} truncate` : ''}`}
            >
              {def.options.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )

      case 'segment':
        return (
          <SegmentGroup
            label={hideInlineLabel ? undefined : def.label}
            options={def.options}
            value={filters.values[def.key] ?? ''}
            onChange={v => filters.setFilter(def.key, v)}
            color={def.color}
          />
        )

      case 'date-range': {
        const fromLabel = def.label ? `${def.label} — desde` : 'Desde'
        const toLabel = def.label ? `${def.label} — hasta` : 'Hasta'
        const fromVal = filters.values[`${def.key}_from`] ?? ''
        const toVal = filters.values[`${def.key}_to`] ?? ''
        const PRESETS = def.presets
          ? [
              { key: 'month', label: 'Este mes', range: currentMonthRange },
              { key: 'last-month', label: 'Mes anterior', range: lastMonthRange },
              { key: 'year', label: 'Este año', range: currentYearRange },
            ]
          : []
        const activePreset = PRESETS.find(p => {
          const r = p.range()
          return r.from === fromVal && r.to === toVal
        })?.key
        return (
          <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label={def.label}>
            {!hideInlineLabel && def.label && <span className="text-xs font-medium text-content-subtle" aria-hidden="true">{def.label}</span>}
            {PRESETS.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => { const r = p.range(); filters.setRange(def.key, r.from, r.to) }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  activePreset === p.key
                    ? 'bg-primary text-on-primary'
                    : 'bg-soft text-content-muted hover:bg-canvas'
                }`}
              >
                {p.label}
              </button>
            ))}
            <DatePicker
              value={filters.values[`${def.key}_from`] ?? ''}
              onChange={v => filters.setFilter(`${def.key}_from`, v)}
              placeholder="Desde"
              aria-label={fromLabel}
              className="w-[120px] px-2 py-1.5 text-xs border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
            />
            <DatePicker
              value={filters.values[`${def.key}_to`] ?? ''}
              onChange={v => filters.setFilter(`${def.key}_to`, v)}
              placeholder="Hasta"
              aria-label={toLabel}
              className="w-[120px] px-2 py-1.5 text-xs border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
            />
          </div>
        )
      }

      case 'number-range': {
        const minLabel = def.label ? `${def.label} — mínimo` : 'Mínimo'
        const maxLabel = def.label ? `${def.label} — máximo` : 'Máximo'
        return (
          <div className="flex items-center gap-1.5" role="group" aria-label={def.label}>
            {!hideInlineLabel && def.label && <span className="text-xs font-medium text-content-subtle" aria-hidden="true">{def.label}</span>}
            <input
              type="number"
              value={filters.values[`${def.key}_min`] ?? ''}
              onChange={e => filters.setFilter(`${def.key}_min`, e.target.value)}
              placeholder="Mín"
              aria-label={minLabel}
              className="w-24 px-2 py-1.5 text-xs border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
            />
            <input
              type="number"
              value={filters.values[`${def.key}_max`] ?? ''}
              onChange={e => filters.setFilter(`${def.key}_max`, e.target.value)}
              placeholder="Máx"
              aria-label={maxLabel}
              className="w-24 px-2 py-1.5 text-xs border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
            />
          </div>
        )
      }

      default:
        return null
    }
  })()

  // Apilado: rótulo sobre el control (search no lleva rótulo arriba).
  if (stacked && def.type !== 'search' && def.label) {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={uid} className="text-xs font-medium text-content-subtle">{def.label}</label>
        {control}
      </div>
    )
  }

  return control
}
